import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import { FaCheck, FaSearch, FaMoneyBillWave } from 'react-icons/fa';
import { formatRupiah } from '../utils/formatter';

const DataPengembalian = () => {
    const [pengembalian, setPengembalian] = useState([]);
    const [peminjamanMenungguVerifikasi, setPeminjamanMenungguVerifikasi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('menunggu'); // 'menunggu' or 'selesai'

    // State untuk search dan pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // State untuk modal verifikasi
    const [isVerifikasiModalOpen, setIsVerifikasiModalOpen] = useState(false);
    const [selectedPeminjamanId, setSelectedPeminjamanId] = useState(null);
    const [selectedPeminjamanData, setSelectedPeminjamanData] = useState(null);
    const [verifikasiData, setVerifikasiData] = useState({
        kondisi_buku: 'baik',
        catatan_admin: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch pengembalian yang sudah selesai
            const pengembalianResponse = await axios.get(`${process.env.REACT_APP_API_URL}/peminjaman/pengembalian`);
            
            // Fetch peminjaman yang menunggu verifikasi pengembalian
            const peminjamanResponse = await axios.get(`${process.env.REACT_APP_API_URL}/peminjaman`);
            const menungguVerifikasi = peminjamanResponse.data.filter(
                item => item.status_peminjaman === 'Diajukan Pengembalian'
            );
            
            setPengembalian(pengembalianResponse.data);
            setPeminjamanMenungguVerifikasi(menungguVerifikasi);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire('Gagal', 'Gagal memuat data pengembalian. Silakan coba lagi.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Data yang ditampilkan berdasarkan tab
    const dataToDisplay = activeTab === 'menunggu' ? peminjamanMenungguVerifikasi : pengembalian;

    // Filter data berdasarkan search term
    const filteredData = dataToDisplay.filter(item =>
        item.nama_anggota.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.judul_buku.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const totalEntries = filteredData.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentEntries = filteredData.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, entriesPerPage, activeTab]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleEntriesPerPageChange = (e) => {
        setEntriesPerPage(parseInt(e.target.value));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleVerifikasiPengembalian = (item) => {
        setSelectedPeminjamanId(item.id);
        setSelectedPeminjamanData(item);
        setVerifikasiData({
            kondisi_buku: 'baik',
            catatan_admin: ''
        });
        setIsVerifikasiModalOpen(true);
    };

    const handleSubmitVerifikasi = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/peminjaman/pengembalian/${selectedPeminjamanId}`,
                {
                    keterangan: 'Buku telah dikembalikan dan diverifikasi oleh admin',
                    kondisi_buku: verifikasiData.kondisi_buku,
                    catatan_admin: verifikasiData.catatan_admin
                }
            );

            const denda = response.data.denda;
            const kondisi = response.data.kondisi_buku;

            let message = `Verifikasi berhasil. Kondisi buku: ${kondisi}.`;
            if (denda > 0) {
                message += ` Denda keterlambatan: ${formatRupiah(denda)}.`;
            } else {
                message += ` Tidak ada denda.`;
            }

            Swal.fire('Berhasil!', message, 'success');
            setIsVerifikasiModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error verifying pengembalian:', error);
            Swal.fire('Gagal', 'Gagal memproses verifikasi pengembalian.', 'error');
        }
    };

    const handleBayarDenda = async (idPengembalian) => {
        const result = await Swal.fire({
            title: 'Konfirmasi Pembayaran Denda',
            text: 'Apakah denda sudah dibayar oleh anggota?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#48c78e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ya, Sudah Dibayar',
            cancelButtonText: 'Batal'
        });

        if (result.isConfirmed) {
            try {
                await axios.put(`${process.env.REACT_APP_API_URL}/peminjaman/pengembalian/denda/${idPengembalian}`);
                Swal.fire('Berhasil!', 'Status denda berhasil diperbarui.', 'success');
                fetchData();
            } catch (error) {
                console.error('Error updating denda:', error);
                Swal.fire('Gagal', 'Gagal memperbarui status denda.', 'error');
            }
        }
    };

    const renderPagination = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i}>
                    <button
                        className={`pagination-link ${i === currentPage ? 'is-current' : ''}`}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return pages;
    };

    if (loading) {
        return (
            <Layout title="Data Pengembalian">
                <p>Memuat data pengembalian...</p>
            </Layout>
        );
    }

    return (
        <Layout title="Data Pengembalian">
            <div className="box">
                {/* Tabs */}
                <div className="tabs is-boxed">
                    <ul>
                        <li className={activeTab === 'menunggu' ? 'is-active' : ''}>
                            <a onClick={() => setActiveTab('menunggu')}>
                                <span className="icon">
                                    <i className="fas fa-clock"></i>
                                </span>
                                <span>Menunggu Verifikasi ({peminjamanMenungguVerifikasi.length})</span>
                            </a>
                        </li>
                        <li className={activeTab === 'selesai' ? 'is-active' : ''}>
                            <a onClick={() => setActiveTab('selesai')}>
                                <span className="icon">
                                    <i className="fas fa-check-circle"></i>
                                </span>
                                <span>Riwayat Pengembalian</span>
                            </a>
                        </li>
                    </ul>
                </div>

                {/* Alert jika ada yang menunggu verifikasi */}
                {activeTab === 'menunggu' && peminjamanMenungguVerifikasi.length > 0 && (
                    <div className="notification is-warning is-light mb-4">
                        <strong>⚠️ Perhatian:</strong> Ada {peminjamanMenungguVerifikasi.length} pengembalian buku yang menunggu verifikasi Anda.
                    </div>
                )}

                <div className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <div className="field has-addons">
                                <p className="control">
                                    <span className="select">
                                        <select value={entriesPerPage} onChange={handleEntriesPerPageChange}>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </span>
                                </p>
                                <p className="control">
                                    <span className="button is-static">entri</span>
                                </p>
                            </div>
                        </div>
                        <div className="level-item">
                            <span className="has-text-grey">
                                Menampilkan {startIndex + 1} sampai {Math.min(endIndex, totalEntries)} dari {totalEntries} entri
                            </span>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <div className="control has-icons-right">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Cari..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    style={{ minWidth: '250px' }}
                                />
                                <span className="icon is-small is-right">
                                    <FaSearch />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable">
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Nama Anggota</th>
                                <th>Judul Buku</th>
                                <th>Tgl. Peminjaman</th>
                                <th>Tgl. Jatuh Tempo</th>
                                {activeTab === 'selesai' && <th>Tgl. Pengembalian</th>}
                                {activeTab === 'selesai' && <th>Denda</th>}
                                {activeTab === 'selesai' && <th>Kondisi Buku</th>}
                                {activeTab === 'selesai' && <th>Status Denda</th>}
                                <th>Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentEntries.length > 0 ? (
                                currentEntries.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>{item.nama_anggota}</td>
                                        <td>{item.judul_buku}</td>
                                        <td>{new Date(item.tanggal_peminjaman).toLocaleDateString('id-ID')}</td>
                                        <td>{new Date(item.tanggal_kembali).toLocaleDateString('id-ID')}</td>
                                        
                                        {activeTab === 'selesai' && (
                                            <>
                                                <td>{new Date(item.tanggal_pengembalian).toLocaleDateString('id-ID')}</td>
                                                <td>
                                                    <span className={item.denda > 0 ? 'has-text-danger has-text-weight-bold' : 'has-text-success'}>
                                                        {formatRupiah(item.denda)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`tag ${
                                                        item.kondisi_buku === 'baik' ? 'is-success' : 
                                                        item.kondisi_buku === 'rusak' ? 'is-warning' : 
                                                        'is-danger'
                                                    }`}>
                                                        {item.kondisi_buku === 'baik' ? 'Baik' : 
                                                         item.kondisi_buku === 'rusak' ? 'Rusak' : 
                                                         'Hilang'}
                                                    </span>
                                                    {item.catatan_admin && (
                                                        <p className="help is-size-7" title={item.catatan_admin}>
                                                            {item.catatan_admin.length > 30 
                                                                ? item.catatan_admin.substring(0, 30) + '...' 
                                                                : item.catatan_admin}
                                                        </p>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`tag ${item.status_denda === 'Selesai' ? 'is-success' : 'is-warning'}`}>
                                                        {item.status_denda}
                                                    </span>
                                                </td>
                                            </>
                                        )}
                                        
                                        <td>
                                            {/* ✅ PERBAIKAN: Aksi untuk tab menunggu - hanya verifikasi */}
                                            {activeTab === 'menunggu' && (
                                                <button
                                                    className="button is-success is-small"
                                                    onClick={() => handleVerifikasiPengembalian(item)}
                                                >
                                                    <span className="icon">
                                                        <FaCheck />
                                                    </span>
                                                    <span>Verifikasi</span>
                                                </button>
                                            )}
                                            
                                            {/* ✅ PERBAIKAN: Aksi untuk tab selesai - hanya bayar denda jika belum bayar */}
                                            {activeTab === 'selesai' && item.status_denda === 'Belum Bayar' && item.denda > 0 && (
                                                <button
                                                    className="button is-info is-small"
                                                    onClick={() => handleBayarDenda(item.id)}
                                                >
                                                    <span className="icon">
                                                        <FaMoneyBillWave />
                                                    </span>
                                                    <span>Bayar Denda</span>
                                                </button>
                                            )}
                                            
                                            {/* ✅ PERBAIKAN: Tidak ada aksi jika sudah selesai atau tidak ada denda */}
                                            {activeTab === 'selesai' && (item.status_denda === 'Selesai' || item.denda === 0) && (
                                                <span className="tag is-light">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={activeTab === 'menunggu' ? '6' : '10'} className="has-text-centered">
                                        {searchTerm 
                                            ? 'Tidak ada data yang sesuai dengan pencarian.' 
                                            : activeTab === 'menunggu'
                                                ? 'Tidak ada pengembalian yang menunggu verifikasi.'
                                                : 'Tidak ada data pengembalian.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="pagination is-centered mt-4" role="navigation" aria-label="pagination">
                        <button 
                            className="pagination-previous" 
                            onClick={() => handlePageChange(currentPage - 1)} 
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <button 
                            className="pagination-next" 
                            onClick={() => handlePageChange(currentPage + 1)} 
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                        <ul className="pagination-list">
                            {renderPagination()}
                        </ul>
                    </nav>
                )}
            </div>

            {/* Modal Verifikasi Pengembalian */}
            <div className={`modal ${isVerifikasiModalOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={() => setIsVerifikasiModalOpen(false)}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">Verifikasi Pengembalian Buku</p>
                        <button className="delete" onClick={() => setIsVerifikasiModalOpen(false)}></button>
                    </header>
                    <section className="modal-card-body">
                        {selectedPeminjamanData && (
                            <div className="content">
                                <div className="notification is-light mb-4">
                                    <p><strong>Nama Anggota:</strong> {selectedPeminjamanData.nama_anggota}</p>
                                    <p><strong>Judul Buku:</strong> {selectedPeminjamanData.judul_buku}</p>
                                    <p><strong>Tanggal Peminjaman:</strong> {new Date(selectedPeminjamanData.tanggal_peminjaman).toLocaleDateString('id-ID')}</p>
                                    <p><strong>Tanggal Jatuh Tempo:</strong> {new Date(selectedPeminjamanData.tanggal_kembali).toLocaleDateString('id-ID')}</p>
                                </div>
                                
                                <div className="field">
                                    <label className="label">Kondisi Buku *</label>
                                    <div className="control">
                                        <div className="select is-fullwidth">
                                            <select 
                                                value={verifikasiData.kondisi_buku}
                                                onChange={(e) => setVerifikasiData({...verifikasiData, kondisi_buku: e.target.value})}
                                            >
                                                <option value="baik">Baik</option>
                                                <option value="rusak">Rusak</option>
                                                <option value="hilang">Hilang</option>
                                            </select>
                                        </div>
                                    </div>
                                    <p className="help is-info">
                                        Pilih kondisi buku saat dikembalikan untuk keperluan laporan
                                    </p>
                                </div>
                                
                                <div className="field">
                                    <label className="label">Catatan Admin (Opsional)</label>
                                    <div className="control">
                                        <textarea 
                                            className="textarea"
                                            placeholder="Tambahkan catatan jika diperlukan, misal: halaman 10-15 sobek, cover rusak, dll..."
                                            value={verifikasiData.catatan_admin}
                                            onChange={(e) => setVerifikasiData({...verifikasiData, catatan_admin: e.target.value})}
                                            rows="3"
                                        />
                                    </div>
                                </div>

                                <div className="notification is-info is-light">
                                    <p><strong>Informasi:</strong></p>
                                    <ul>
                                        <li>• Denda dihitung otomatis berdasarkan keterlambatan (Rp 5.000/hari)</li>
                                        <li>• Kondisi buku hanya untuk laporan, tidak mempengaruhi denda</li>
                                        <li>• Siswa dengan buku rusak/hilang akan tercatat di laporan</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </section>
                    
                    <footer className="modal-card-foot">
                        <button className="button is-success" onClick={handleSubmitVerifikasi}>
                            <span className="icon">
                                <FaCheck />
                            </span>
                            <span>Verifikasi Pengembalian</span>
                        </button>
                        <button className="button" onClick={() => setIsVerifikasiModalOpen(false)}>
                            Batal
                        </button>
                    </footer>
                </div>
            </div>
        </Layout>
    );
};

export default DataPengembalian;